import { useCallback, useEffect, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { EmbeddedStripeCheckoutSession } from '@/molecules/EmbeddedStripeCheckoutSession'
import { getStringFromSessionStorage } from '@/utils/session-storage'
import { useTranslate } from '@/utils/translate/translate-client'
import { Page } from '../../components/Page'
import {
  TorchApplicationStatus,
  TorchApplicationTypes,
  useTorchApplication,
} from '../../components/TorchApplicationContext'
import { CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT } from '../../queries'
import { useUserGuildProjectQuery } from '../AssociatedProject/hooks/useUserGuildProjectQuery'
import { SubmissionSuccess } from './SubmissionSuccess'

interface GetTorchPaymentPriorPageProps {
  isEpisode: boolean
  hasProjects: boolean
}

const getTorchPaymentPriorPage = ({ isEpisode, hasProjects }: GetTorchPaymentPriorPageProps) => {
  if (isEpisode && hasProjects) return 5

  return 4
}

export const TorchApplicationPayment = () => {
  const { t } = useTranslate('common')
  const { query, push } = useRouter()
  const { setPage, data, id, type, applicationProjectId, updateStatus } = useTorchApplication()
  const isEpisode = useMemo(() => type === TorchApplicationTypes.CompletedEpisode, [type])

  const [createSession, { data: checkoutSession, loading }] = useMutation(CREATE_TORCH_APPLICATION_STRIPE_CHECKOUT, {
    errorPolicy: 'all',
  })
  const { data: projectData } = useUserGuildProjectQuery()
  const hasPaid = !!data?.paymentDate || query.success

  const handleSubmission = useCallback(() => {
    updateStatus(TorchApplicationStatus.PendingReview)
    push('/guild/create/torch')
  }, [push, updateStatus])

  useEffect(() => {
    const promoCode = getStringFromSessionStorage('torchSubmissionPromoCode')
    if (!checkoutSession && id && !hasPaid) {
      createSession({ variables: { input: { id, promoCode } } })
    }
  }, [checkoutSession, createSession, id, hasPaid])

  const isEpisodeProjectPassed = useMemo(() => {
    return (
      projectData?.guildUserProjects?.find((project) => project?.id === applicationProjectId && project?.isPassed) &&
      isEpisode
    )
  }, [applicationProjectId, isEpisode, projectData?.guildUserProjects])

  return (
    <Page
      disabled={!hasPaid}
      onBack={() =>
        setPage(getTorchPaymentPriorPage({ isEpisode, hasProjects: !!projectData?.guildUserProjects?.length }))
      }
    >
      {isEpisodeProjectPassed && (
        <Button variant="oxide" onClick={handleSubmission} className="w-72">
          {t('submitApplication', 'Submit Application')}
        </Button>
      )}
      {!isEpisodeProjectPassed && hasPaid && <SubmissionSuccess />}
      {!isEpisodeProjectPassed && !hasPaid && (
        <div className="min-h-560 w-full max-w-[450px] lg:max-w-[1000px]">
          <EmbeddedStripeCheckoutSession
            clientSecret={checkoutSession?.createTorchApplicationStripeCheckout?.clientSecret}
            loading={loading}
          />
        </div>
      )}
    </Page>
  )
}
